import { formatWeekdayDateOrDash } from "@shared-core/domain/formatters";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { Download, FileText } from "lucide-react";
import { useFileDownload, type FetchFile } from "./useFetchedFile";

// The Documents tab, the same on the quote and the order page (Ben, 5 Oct):
// one row per PDF. A quote has its own PDF; an order's are the sales
// ledger's (its pro-forma, its invoices, the receipt once paid up front, a
// credit note), linked through the customer's own link, never attached to an
// email.

export interface DocumentRow {
  key: string;
  /** "Quote", "Card receipt", "Invoice", "Credit note", "Delivery note". */
  kind: string;
  label: string;
  dated: string | null;
  /** The API-relative address the PDF is fetched from; null on a page that downloads nothing (the staff view-as mirror). */
  url: string | null;
}

function DocumentDownload({
  row,
  fetchFile,
}: {
  row: DocumentRow & { url: string };
  fetchFile: FetchFile;
}) {
  const { isBusy, hasFailed, download } = useFileDownload(fetchFile);
  return (
    <span className="flex flex-col items-end gap-1">
      <Button
        variant="outline"
        size="sm"
        disabled={isBusy}
        aria-busy={isBusy}
        onClick={() => void download(row.url, `${row.key}.pdf`)}
      >
        <Download className="size-4" />
        {isBusy ? "Preparing PDF…" : "Download PDF"}
      </Button>
      {hasFailed && (
        <span role="alert" className="text-xs text-red-700 dark:text-red-400">
          We couldn&apos;t download that just now. Try again in a moment.
        </span>
      )}
    </span>
  );
}

export function DocumentsList({
  documents,
  emptySentence,
  fetchFile,
}: {
  documents: DocumentRow[];
  emptySentence: string;
  /** Null on a page that downloads nothing. */
  fetchFile: FetchFile | null;
}) {
  return (
    <Card className="gap-3 p-5">
      {documents.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptySentence}</p>
      ) : (
        <ul className="divide-y">
          {documents.map((d) => (
            <li
              key={d.key}
              className="flex flex-wrap items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
            >
              <span className="flex min-w-0 items-start gap-2 text-sm">
                <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <span>
                  <span className="font-medium">{d.kind}</span> · {d.label}
                  {d.dated && (
                    <span className="block text-xs text-muted-foreground">
                      {formatWeekdayDateOrDash(d.dated)}
                    </span>
                  )}
                </span>
              </span>
              {d.url && fetchFile && (
                <DocumentDownload
                  row={{ ...d, url: d.url }}
                  fetchFile={fetchFile}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
